/**
 * Move the reading position to the routed view's `<main>`.
 *
 * The complication is lazy routes. On the first visit to a view whose chunk is
 * not cached, the `<main>` on screen belongs to the Suspense fallback: focusing
 * it is correct for that moment, but when the chunk resolves React REPLACES
 * that element, and focus falls back to `<body>`. The route did not change, so
 * the effect that moved focus does not run again, and a keyboard or screen
 * reader user is silently returned to the top of the document.
 *
 * So: focus what is there now, and if it is still loading, watch for the real
 * `<main>` and move focus once more when it arrives. Bounded, single-shot, and
 * cancellable, because a reader who has since clicked something else must not
 * have focus yanked out from under them.
 */

/** Give up watching after this long: a chunk this slow has other problems. */
const SETTLE_TIMEOUT_MS = 10_000;

function focusEl(el: HTMLElement): void {
  el.setAttribute("tabindex", "-1");
  el.focus({ preventScroll: true });
}

/**
 * Focus `#main`. Returns a cleanup function that cancels any pending
 * re-focus; callers should invoke it from their effect teardown.
 */
export function focusMainWhenReady(): () => void {
  if (typeof document === "undefined") return () => {};
  const el = document.getElementById("main");
  if (el) focusEl(el);

  // Settled already: nothing more to do. A MISSING #main is not settled: the
  // routed view has not committed yet, so keep watching for it.
  if (el && el.getAttribute("aria-busy") !== "true") return () => {};

  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    observer.disconnect();
    clearTimeout(timer);
  };

  const observer = new MutationObserver(() => {
    const next = document.getElementById("main");
    if (!next || next.getAttribute("aria-busy") === "true") return;
    // Only take focus back if the reader has not moved it themselves in the
    // meantime: body / null means the fallback's element was removed and the
    // browser had nowhere to put it.
    const active = document.activeElement;
    if (active === null || active === document.body) focusEl(next);
    finish();
  });
  // childList catches the fallback being replaced; attributes catch a view
  // that settles in place by dropping aria-busy on the same element.
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["aria-busy"],
  });
  const timer = setTimeout(finish, SETTLE_TIMEOUT_MS);

  return finish;
}
